import React, { useCallback, useState } from 'react';
import { X, RefreshCw } from 'lucide-react';
import { Textarea } from '../../ui/textarea';

export const FIGURE_REVISION_NOTES_MAX_LENGTH = 1000;

interface RegenerateFigureDialogProps {
    figureKey: string;
    onClose: () => void;
    onConfirm: (revisionNotes: string) => void | Promise<void>;
}

const viewStates = {
    ready: 0,
    submitting: 1
};

export function RegenerateFigureDialog({
    figureKey,
    onClose,
    onConfirm
}: RegenerateFigureDialogProps) {
    const [viewState, setViewState] = useState(viewStates.ready);
    const [revisionNotes, setRevisionNotes] = useState('');

    const handleClose = useCallback(() => {
        if (viewState === viewStates.submitting) return;
        setViewState(viewStates.ready);
        onClose();
    }, [onClose, viewState]);

    const handleRegenerate = async () => {
        setViewState(viewStates.submitting);
        try {
            await onConfirm(revisionNotes.trim());
            onClose();
        } catch (error) {
            console.error('Error regenerating figure:', error);
            setViewState(viewStates.ready);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">Regenerate image</h2>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            disabled={viewState === viewStates.submitting}
                            aria-label="Close"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="space-y-4">
                        <p className="text-sm text-gray-700">
                            Create a new AI image for <strong>{figureKey}</strong>. Optionally say how it
                            should differ from the current one.
                        </p>

                        <div>
                            <label
                                htmlFor="figure-revision-notes"
                                className="block text-sm font-medium text-gray-900 mb-1.5"
                            >
                                How should the next image be different? (optional)
                            </label>
                            <Textarea
                                id="figure-revision-notes"
                                value={revisionNotes}
                                onChange={e =>
                                    setRevisionNotes(
                                        e.target.value.slice(0, FIGURE_REVISION_NOTES_MAX_LENGTH)
                                    )
                                }
                                placeholder="e.g. Show a side view, fewer labels, no people"
                                rows={4}
                                disabled={viewState === viewStates.submitting}
                                className="resize-y"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                {revisionNotes.length}/{FIGURE_REVISION_NOTES_MAX_LENGTH}
                            </p>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-6">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            disabled={viewState === viewStates.submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => void handleRegenerate()}
                            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            disabled={viewState === viewStates.submitting}
                        >
                            <RefreshCw
                                size={16}
                                className={`mr-2 ${viewState === viewStates.submitting ? 'animate-spin' : ''}`}
                            />
                            {viewState === viewStates.submitting ? 'Regenerating...' : 'Regenerate'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
